import {
  Button,
  Container,
  Grid2,
  IconButton,
  Slider,
  Stack,
} from "@mui/material";
import { HeaderView } from "../../component/HeaderView";
import { useTranslation } from "react-i18next";
import { PlayedGamesViewModel } from "./PlayedGamesViewModel";
import { CustomCardView } from "../../component/CustomCardView";
import { CustomTextView } from "../../component/CustomTextView";
import moment from "moment";
import "moment/locale/ar";
import {
  COLOR_LIGHT_GRAY,
  COLOR_MAIN,
  COLOR_WHITE,
} from "../../../data/colors/Colors";
import { LoadingView } from "../../component/LoadingView";
import { ArrowBackRounded, ArrowForwardRounded } from "@mui/icons-material";
import { Category, CategoryEntity } from "../../../domain/model/Category";
import { CustomImageView } from "../../component/CustomImageView";
import useWindowDimensions from "../../../core/utils/UseWindowDimensions";
import { Game } from "../../../domain/model/Game";
import { EmptyView } from "../../component/EmptyView";
import { Player } from "../../../domain/model/User";
import { useEffect } from "react";

export function PlayedGamesView({ player }: { player: Player | undefined }) {
  const viewModel = PlayedGamesViewModel();
  const { t } = useTranslation();

  const games = player?.games;
  return (
    <Stack>
      <HeaderView
        title={t("games")}
        isLayoutFullWidthInSmallerScreens={false}
      />

      {!games ? (
        <LoadingView />
      ) : (
        <>
          {games.length == 0 ? (
            <EmptyView />
          ) : (
            <Container maxWidth={false}>
              <Grid2 container spacing={2} pb={3} pt={3}>
                {games?.map((game, index) => {
                  return (
                    <Grid2
                      size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}
                      key={game._id}
                    >
                      <GameView
                        game={game}
                        index={index}
                        onGameClicked={() => viewModel.onGameClicked(game)}
                      />
                    </Grid2>
                  );
                })}
              </Grid2>
            </Container>
          )}{" "}
        </>
      )}
    </Stack>
  );
}

function GameView({
  game,
  index,
  onGameClicked,
}: {
  game: Game;
  index: number;
  onGameClicked: () => void;
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() == "rtl";

  function getProgressOfTheGame(): number {
    if (!game.questionsList) return 0;
    return game.questionsList.filter((q) => q != undefined).length;
  }

  return (
    <CustomCardView
      padding={0}
      children={
        <Button
          sx={{ textTransform: "none", p: 3 }}
          fullWidth
          onClick={onGameClicked}
        >
          <Stack width={"100%"} alignItems={"center"} gap={2}>
            <Stack
              width={"100%"}
              alignItems={"center"}
              direction={"row"}
              justifyContent={"space-between"}
              gap={1}
            >
              <CustomTextView
                text={`#${index + 1}`}
                variant="h5"
                fontWeight="bold"
                textColor={COLOR_LIGHT_GRAY}
              />
              <Stack
                alignItems={"center"}
                gap={1}
                justifyContent={"center"}
                direction={"row"}
              >
                <CustomTextView text={t("played")} variant="body1" />
                <CustomTextView
                  text={moment(game.date)
                    .locale(i18n.resolvedLanguage ?? "ar")
                    .fromNow()}
                  variant="body1"
                  textColor={COLOR_LIGHT_GRAY}
                />
              </Stack>
              <IconButton sx={{ border: "solid 1px black" }}>
                {isRtl && <ArrowBackRounded />}
                {!isRtl && <ArrowForwardRounded />}
              </IconButton>
            </Stack>

            <Stack
              width={"100%"}
              alignItems={"center"}
              direction={"row"}
              justifyContent={"space-between"}
              gap={1}
            >
              <Stack alignItems={"start"} gap={1}>
                <CustomTextView text={t("first_team")} variant="body2" />
                <CustomTextView
                  text={game.firstTeamName ?? "NAMELESS 1"}
                  variant="body1"
                  fontWeight="bold"
                />
              </Stack>
              <Stack alignItems={"center"} gap={1}>
                <CustomTextView text={t("total_score")} variant="h5" />
                <CustomTextView
                  text={game.score?.toString()}
                  variant="h4"
                  fontWeight="bold"
                />
              </Stack>
              <Stack alignItems={"end"} gap={1}>
                <CustomTextView text={t("second_team")} variant="body2" />
                <CustomTextView
                  text={game.secondTeamName ?? "NAMELESS 2"}
                  variant="body1"
                  fontWeight="bold"
                />
              </Stack>
            </Stack>

            <Slider
              value={getProgressOfTheGame() / 36}
              sx={{
                color: COLOR_MAIN,
                width: "100%",
                "& .MuiSlider-thumb": {
                  display: "none",
                },
              }}
              min={0}
              max={1}
            />

            <Grid2 container spacing={1}>
              {game.selectedCategories.map((c) => {
                return (
                  <Grid2 key={c.category._id} size={4}>
                    <CategoryItemView category={c.category} />
                  </Grid2>
                );
              })}
            </Grid2>
          </Stack>
        </Button>
      }
    />
  );
}

export function CategoryItemView({
  category,
  height,
  textVariant,
  textBgColor,
}: {
  category: CategoryEntity | Category;
  height?: string | number;
  textVariant?: "h6" | "caption";
  textBgColor?: string;
}) {
  const { width } = useWindowDimensions();
  const isMobile = width < 700;
  return (
    <Stack
      width={"100%"}
      alignItems={"center"}
      sx={{
        border: "solid 1.5px " + (textBgColor ? textBgColor : COLOR_MAIN),
        borderRadius: 4,
        boxShadow: 2,
        position: "relative",
        height: height != undefined ? height : isMobile ? 120 : 200,
      }}
    >
      <CustomImageView
        src={category.imageUrl}
        width={"100%"}
        height={"100%"}
        scale="contain"
        props={{
          borderRadius: 3.5,
          background: "white",
        }}
      />
      <Stack
        sx={{
          position: "absolute",
          bottom: 0,
          zIndex: 2,
          background: textBgColor ? textBgColor : "rgba(135,40,77,0.8)",
          width: "100%",
          alignItems: "center",
          borderRadius: 3.5,
          p: 0.2,
        }}
      >
        <CustomTextView
          text={category.title}
          textColor={COLOR_WHITE}
          fontWeight="bold"
          variant={textVariant ?? "h6"}
          textAlign="center"
        />
      </Stack>
    </Stack>
  );
}
